// screens/Clark/answer/authorityLink.mjs — BUNDLE 2 · STEP 4. The deep link, on its own.
//
// 🔴 IT IS A SEPARATE `.mjs` FOR THE SAME REASON THE ROUTER IS, AND THE REASON IS TONIGHT'S DEFECT.
// `AuthorityCard.jsx` cannot be imported by `node --test` (no JSX loader), so anything living
// inside it is unassertable — which is precisely how a nine-regex whitelist sat untested in front
// of the whole research path for three bundles. `researchHref` is pure logic about identity, not
// markup, and getting it wrong sends a lawyer to the WRONG CASE. So it lives where it can be
// tested, and the component imports it.

/**
 * ═══ RUN CT — `researchHref` MOVED TO `@citeclerk/ui/authorityLink`, AND THIS IS ITS RE-EXPORT ═══
 *
 * 🔴 THE NAME STILL RESOLVES HERE, DELIBERATELY. Nine call sites and five test files import it from
 * this path. Re-exporting keeps every one of them true while the DEFINITION lives in exactly one
 * place — which is the invariant `clarkAuthorityParity.test.js` has always been about, and it now
 * spans two apps instead of one.
 *
 * ⭐ WHY IT HAD TO MOVE AT ALL: `AuthorityModal.jsx` was promoted into `packages/ui` so Clark and
 * Research render the SAME modal rather than two that drift. The modal draws `Open in Research`, so
 * the link builder went with it. The alternative — letting steel hand-roll its own `/research/case/`
 * URL — is the exact wrong-case defect that test forbids, so it was never on the table.
 *
 * ⚠️ THE OTHER TWO FUNCTIONS IN THIS FILE DID NOT MOVE, and that is not an oversight.
 * `storedAnswerHref` is about Clark's stored-answer route and `hasOpenableAuthorities` is about
 * Clark's zero-state gate. Research asks neither. Only the shared thing is shared.
 */
export { researchHref } from "@citeclerk/ui/authorityLink";


/**
 * BUNDLE F · LANE 2 — the link `Open in Research` has been waiting three bundles for.
 *
 * 🔴 IT TAKES AN ID AND NOTHING ELSE, AND THAT IS THE WHOLE POINT. The tempting alternative is
 * `/research/answer?q=<question>`, which LOOKS equivalent and is not: that route RE-RUNS the
 * question, through the public answer path, which SWEEPS. Clark reads cache-only (`sweep: false`).
 * So a click-through would put the same question in front of the same user twice in the same
 * minute and let them watch a good-law badge change — the exact defect the parity work exists to
 * prevent. `/research/answer/<id>` renders the STORED payload: one SELECT, no engine, no sweep.
 *
 * 🔴 NULL IS THE CORRECT ANSWER WHEN THERE IS NO STORED ROW, and callers MUST render no control
 * rather than a dead one. `answerId` is null for every answer Steel did not cache — a decline, an
 * unknown ranker, a failed write. This function existing does not mean the button always shows;
 * it means the button shows exactly when it works. A control that lies about having acted is worse
 * than no control, and this button spent three bundles being that control.
 *
 * The two guards below are BOTH load-bearing, which a non-vacuity probe proved the interesting way:
 * removing only the null check changes nothing, because `String(null)` fails the numeric test and
 * the function still returns null. Removing both is what breaks it. Do not delete either on the
 * grounds that the other covers it.
 *
 * Same-origin relative path, for the reason documented above `researchHref`: `citeclerk.com` is
 * one origin behind Caddy and the `/research` basename lives at the router, so an absolute origin
 * would produce the URL that 404s.
 */
export function storedAnswerHref(answerId) {
  if (answerId == null || answerId === "") return null;
  const s = String(answerId);
  // Steel's ids are bigint. Anything else is a bug upstream, and a link built from it would be a
  // 404 at best — so refuse to build one rather than send a lawyer somewhere that isn't an answer.
  if (!/^\d+$/.test(s)) return null;
  return `/research/answer/${s}`;
}


/**
 * ═══ RUN C · AMENDMENT — DOES THIS ANSWER HAVE ANYWHERE TO GO? ════════════════════════════════
 *
 * `Open in Research` and `Show Authorities` are entrances to an authority list. Two of Clark's six
 * zero-state cards — *Generate a citation* and *Explain a Bluebook rule* — produce answers that
 * have no authority list at all, and a third (*Summarize an opinion*) can. On those, the first
 * control loads an empty authorities column and an empty reading surface into a three-pane
 * workspace, and the second opens an empty column.
 *
 * 🔴 A CONTROL THAT IS PRESENT BUT DOES NOTHING MEANINGFUL READS AS BROKEN, NOT AS INAPPLICABLE.
 * This file's own header records the mirror image — an `Open in Research` that spent three bundles
 * inert because no caller passed its handler, and *"a working mechanism with no entrance is
 * indistinguishable from a broken one"*. This is the entrance to nothing.
 *
 * 🔴 IT LIVES HERE, IN AN `.mjs`, RATHER THAN INSIDE THE COMPONENT, SO IT CAN BE EXECUTED BY A TEST.
 * apps/main has no JSX test runner, so a gate written inline in `ClarkAnswerV2.jsx` could only be
 * checked by grepping the source for its own condition — and this repo has already shipped a
 * `ReferenceError` past a test that grepped a file as text. A pure predicate in a module both the
 * component and the test import is the difference between proving the string is there and proving
 * the program runs.
 *
 * 🔴 IT READS THE PAYLOAD, NEVER THE RENDERED ROWS. `badge.resolved` is counted over EVERY resolved
 * authority, including the ones past the 8-item display cap — the same reason the badge itself
 * counts beyond the cap. A gate computed from what is on screen would be decided by the cap, which
 * is a display decision and not a fact about the answer.
 *
 * @param {object} answer  the v2 envelope
 * @returns {boolean}      true when there is an authority list worth opening
 */
export function hasOpenableAuthorities(answer) {
  if (!answer || answer.ok === false) return false;
  const resolved = Number(answer?.badge?.resolved);
  // 🔴 `Number.isFinite`, NOT a truthiness test. `Number(undefined)` is NaN and `Number(null)` is
  // **0** — and this program has already passed a check vacuously on exactly `Number(null) === 0`.
  // A badge that reports `resolved: 0` must be believed (hide the controls); a payload with no
  // badge at all is a different case and falls through to the list.
  if (Number.isFinite(resolved)) return resolved > 0;
  return Array.isArray(answer?.authorities) && answer.authorities.length > 0;
}
